import { Fragment } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import { POST_EDITOR_MIN_INDENT } from './post_editor_constants.js';
import {
  applyIndentToParagraphNode,
  createBodyParagraphWithIndent,
  resolveIndentForInsertAfterBlock,
} from './post_block_indent.js';
import {
  isResourceRowGapParagraph,
  isResourceRowNode,
  paragraphIsEffectivelyEmpty,
  RESOURCE_ROW_GAP_PARAGRAPH_ATTRS,
  RESOURCE_ROW_TITLE_PARAGRAPH_ATTRS,
} from './resource_row_paragraph.js';

export { paragraphIsEffectivelyEmpty };

/**
 * After exiting a block container: focus the next paragraph if one exists, else insert one.
 */
export function resolveExitPlacementAfterBlock(state, $from, blockDepth) {
  const afterBlock = $from.after(blockDepth);
  const $after = state.doc.resolve(afterBlock);
  if ($after.nodeAfter?.type?.name === 'paragraph') {
    return { insertPos: null, selectionPos: afterBlock + 1 };
  }
  return { insertPos: afterBlock, selectionPos: afterBlock + 1 };
}

/**
 * List-style exit: Enter on an empty paragraph exits the enclosing block (blockquote /
 * resource row). First Enter at end of a title line adds an empty title line inside the row.
 */

export function shouldExitContainerOnEnter($from) {
  const parent = $from.parent;
  if (parent.type.name !== 'paragraph') return false;

  if (paragraphIsEffectivelyEmpty(parent)) return true;

  if ($from.parentOffset < parent.content.size) return false;

  return parent.lastChild?.type?.name === 'hardBreak';
}

function normalizeExitPlacement(placement) {
  if (typeof placement === 'number') {
    return { insertPos: placement, selectionPos: placement + 1 };
  }
  return placement;
}

/** Strip trailing hardBreak nodes from the paragraph at $from. */
export function stripTrailingHardBreaksFromParagraph(tr, $from) {
  const depth = $from.depth;
  const parent = $from.parent;
  if (parent.type.name !== 'paragraph') return false;

  const kept = [];
  for (let i = 0; i < parent.childCount; i++) {
    kept.push(parent.child(i));
  }
  while (kept.length > 0 && kept[kept.length - 1].type?.name === 'hardBreak') {
    kept.pop();
  }
  if (kept.length === parent.childCount) return false;

  const from = $from.before(depth) + 1;
  const to = from + parent.content.size;
  tr.replaceWith(from, to, kept.length ? Fragment.from(kept) : Fragment.empty);
  return true;
}

function isBlockquoteAttributionParagraph(paragraph) {
  return paragraph?.attrs?.psQuoteRole === 'attribution';
}

function isResourceRowSubtitleParagraph(paragraph) {
  return paragraph?.attrs?.psRowRole === 'subtitle';
}

function tryDeleteEmptyExitParagraph(tr, $from) {
  const depth = $from.depth;
  const parent = $from.parent;
  if (parent.type.name !== 'paragraph' || !paragraphIsEffectivelyEmpty(parent)) return false;
  if (isResourceRowGapParagraph(parent)) return false;
  if (isBlockquoteAttributionParagraph(parent)) return false;
  if (isResourceRowSubtitleParagraph(parent)) return false;

  const containerDepth = depth - 1;
  if (containerDepth < 1) return false;

  const container = $from.node(containerDepth);
  const canRemove =
    (container.type.name === 'blockquote' || isResourceRowNode(container)) &&
    container.childCount > 1;

  if (!canRemove) return false;

  tr.delete($from.before(depth), $from.after(depth));
  return true;
}

/** Remove exit-line debris before leaving a blockquote or resource row. */
export function applyExitLineCleanup(tr, $from) {
  stripTrailingHardBreaksFromParagraph(tr, $from);

  const mapped = tr.mapping.map($from.pos, -1);
  const $cursor = tr.doc.resolve(Math.min(Math.max(1, mapped), tr.doc.content.size - 1));
  tryDeleteEmptyExitParagraph(tr, $cursor);
}

/**
 * Exit blockquote / resource row on Enter: clean the exit line, insert a body paragraph after
 * the container when needed (or focus an existing paragraph in the same list item).
 *
 * `getExitPlacement` may return a number (legacy) or `{ insertPos, selectionPos }`.
 */
export function runExitContainerOnEnter(editor, getExitPlacement, options = {}) {
  const { getExitContainerDepth } = options;

  return editor
    .chain()
    .focus()
    .command(({ state, tr, dispatch }) => {
      const { $from } = state.selection;
      const containerDepth =
        typeof getExitContainerDepth === 'function' ? getExitContainerDepth($from, state) : -1;
      const containerPos = containerDepth >= 0 ? $from.before(containerDepth) : -1;

      applyExitLineCleanup(tr, $from);

      const mapped = tr.mapping.map($from.pos, -1);
      const $cursor = tr.doc.resolve(Math.min(Math.max(1, mapped), tr.doc.content.size - 1));
      const placement = normalizeExitPlacement(getExitPlacement($cursor, state));
      if (!placement) return false;

      let indentLevel = POST_EDITOR_MIN_INDENT;
      if (containerDepth >= 0) {
        const mappedContainerPos = tr.mapping.map(containerPos);
        const boundedContainerPos = Math.min(
          Math.max(1, mappedContainerPos + 1),
          tr.doc.content.size - 1
        );
        const $container = tr.doc.resolve(boundedContainerPos);
        indentLevel = resolveIndentForInsertAfterBlock($container, containerDepth);
      }

      if (placement.insertPos != null) {
        const paragraph = createBodyParagraphWithIndent(state.schema, indentLevel);
        if (!paragraph) return false;
        tr.insert(placement.insertPos, paragraph);
      }

      const selectionPos = tr.mapping.map(placement.selectionPos, -1);
      const bounded = Math.min(Math.max(1, selectionPos), tr.doc.content.size - 1);
      if (indentLevel > POST_EDITOR_MIN_INDENT) {
        const $sel = tr.doc.resolve(bounded);
        let paragraphPos = null;
        if ($sel.parent.type.name === 'paragraph') {
          paragraphPos = $sel.before($sel.depth);
        } else if ($sel.nodeAfter?.type?.name === 'paragraph') {
          paragraphPos = bounded;
        }
        if (paragraphPos != null) {
          tr = applyIndentToParagraphNode(tr, paragraphPos, indentLevel);
        }
      }
      tr.setSelection(TextSelection.near(tr.doc.resolve(bounded), 1));
      if (dispatch) dispatch(tr);
      return true;
    })
    .run();
}

/**
 * First Enter at end of resource row title.
 * @param {{ asGap?: boolean }} options — when true (row already has a subtitle), inserts a
 *   marked gap line the user can keep between title and subtitle.
 */
export function insertResourceRowContinuationLine(editor, $from, { asGap = false } = {}) {
  const insertPos = $from.after($from.depth);
  const attrs = asGap ? RESOURCE_ROW_GAP_PARAGRAPH_ATTRS : RESOURCE_ROW_TITLE_PARAGRAPH_ATTRS;
  return editor
    .chain()
    .focus()
    .insertContentAt(insertPos, {
      type: 'paragraph',
      attrs,
    })
    .setTextSelection(insertPos + 1)
    .run();
}

/** First Enter at end of a non-empty line in blockquote: split inside the quote. */
export function splitContainerLineOnEnter(editor) {
  return editor.commands.splitBlock();
}
